import React, {
  createContext, useContext, useMemo, useState, useEffect, useCallback
} from 'react';
import api from '../api/client';
import { useNavigate } from 'react-router-dom';

// util: parse JWT payload (base64url)
function parseJwt(token) {
  if (!token) return null;
  try {
    const base = token.split('.')[1];
    const b64 = base.replace(/-/g, '+').replace(/_/g, '/')
      .padEnd(Math.ceil(base.length / 4) * 4, '=');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

function safeParse(json) {
  if (!json || json === 'undefined' || json === 'null') return null;
  try { return JSON.parse(json); } catch { return null; }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    const t = localStorage.getItem('token');
    return !t || t === 'undefined' || t === 'null' ? null : t;
  });

  const [user, setUser] = useState(() => {
    const u = safeParse(localStorage.getItem('user'));
    if (u) return u;
    const payload = parseJwt(localStorage.getItem('token'));
    if (payload?.role) return { role: payload.role };
    return null;
  });

  // mensagem de logout (ex: "Sessão expirada...")
  const [logoutMessage, setLogoutMessage] = useState(null);
  const navigate = useNavigate();

  // login: tenta autenticar e já guarda token/user e navega
  const login = useCallback(async (adm_login, pasw_senha) => {
    const { data } = await api.post('/api/api/login', { adm_login, pasw_senha });

    const t = data.token ?? '';
    localStorage.setItem('token', t);
    setToken(t);

    // preferimos role da resposta; se faltar, tenta do token
    let role = data.role;
    if (!role) {
      const payload = parseJwt(t);
      role = payload?.role;
    }
    const nextUser = role ? { role } : null;

    if (nextUser) {
      localStorage.setItem('user', JSON.stringify(nextUser));
    } else {
      localStorage.removeItem('user');
    }
    setUser(nextUser);

    // limpa mensagens antigas de logout
    setLogoutMessage(null);
    navigate('/gerar');
  }, [navigate]);

  // logout: limpa estado e navega pro login. Pode receber uma mensagem opcional.
  const logout = useCallback((message = null) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    if (message) setLogoutMessage(message);
    navigate('/login');
  }, [navigate]);

  // Interceptor de resposta para capturar 401 e fazer logout sem reload
  useEffect(() => {
    const id = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const config = error?.config;
        if (status === 401 && config && !config.__isRetryRequest) {
          // marca para evitar múltiplos logouts concorrentes
          if (config) config.__isRetryRequest = true;
          logout('Sessão expirada. Faça login novamente.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(id);
    };
  }, [logout]);

  const clearLogoutMessage = useCallback(() => setLogoutMessage(null), []);

  const value = useMemo(() => ({
    token,
    user,
    isAuth: !!token,
    login,
    logout,
    logoutMessage,
    clearLogoutMessage,
  }), [token, user, login, logout, logoutMessage, clearLogoutMessage]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
